import type { AgentMode, RunnerMode } from "@project/shared";

// ── Mode action types ──────────────────────────────────────────────────────

export type ModeAction =
  | { type: "noop" }
  | { type: "restart_query"; newMode: AgentMode }
  | { type: "soft_stop" }
  | { type: "start_auto" };

// ── Task context subset needed by ModeController ───────────────────────────

export interface ModeTaskContext {
  status: string;
  plan: string | null;
  storyPointId: string | null;
  isParentTask?: boolean;
  model: string;
  builderModel?: string | null;
  githubPRUrl?: string | null;
}

// ── ModeController: pure state machine with no I/O ─────────────────────────

export class ModeController {
  private _mode: AgentMode;
  private _hasExitedPlanMode = false;
  private _pendingModeRestart = false;
  private _runnerMode: RunnerMode;
  private _isAuto: boolean;

  constructor(initialMode: AgentMode, runnerMode: RunnerMode = "task", isAuto = false) {
    this._mode = initialMode;
    this._runnerMode = runnerMode;
    this._isAuto = isAuto;
  }

  // ── Getters ────────────────────────────────────────────────────────

  get mode(): AgentMode {
    return this._mode;
  }

  get isAuto(): boolean {
    return this._isAuto;
  }

  get hasExitedPlanMode(): boolean {
    return this._hasExitedPlanMode;
  }

  set hasExitedPlanMode(val: boolean) {
    this._hasExitedPlanMode = val;
  }

  get pendingModeRestart(): boolean {
    return this._pendingModeRestart;
  }

  set pendingModeRestart(val: boolean) {
    this._pendingModeRestart = val;
  }

  /** Effective mode accounting for PM/task defaults */
  get effectiveMode(): AgentMode {
    if (this._mode) return this._mode;
    if (this._runnerMode === "pm") {
      return this._isAuto ? "auto" : "discovery";
    }
    return "building";
  }

  get isReadOnly(): boolean {
    const m = this.effectiveMode;
    if (["discovery", "help"].includes(m)) return true;
    return m === "auto" && !this._hasExitedPlanMode;
  }

  get isAutoPlanning(): boolean {
    return this.effectiveMode === "auto" && !this._hasExitedPlanMode;
  }

  get isBuildCapable(): boolean {
    const m = this.effectiveMode;
    return (
      m === "building" ||
      m === "review" ||
      m === "chat" ||
      (m === "auto" && this._hasExitedPlanMode)
    );
  }

  /**
   * Apply authoritative mode from the server's task context.
   * Called after getTaskContext to override env-var defaults with the
   * actual task state — ensures pods that launch without CONVEYOR_AGENT_MODE
   * still get the correct mode.
   */
  applyServerMode(agentMode: AgentMode | null | undefined, isAuto: boolean | undefined): void {
    if (agentMode) {
      this._mode = agentMode;
      this._isAuto = agentMode === "auto" || !!isAuto;
    } else if (isAuto !== undefined) {
      this._isAuto = isAuto;
      if (isAuto && this._runnerMode === "pm" && !this._mode) {
        this._mode = "auto";
      }
    }

    // Safety net: discovery + isAuto means the server didn't set agentMode correctly
    if (this._isAuto && this._mode === "discovery") {
      this._mode = "auto";
    }
  }

  // ── Mode resolution ────────────────────────────────────────────────

  /** Resolve the initial mode based on task context */
  resolveInitialMode(context: ModeTaskContext): AgentMode {
    // Code-review runner maps to unified review mode
    if (this._runnerMode === "code-review") {
      this._mode = "review";
      return this._mode;
    }

    // Auto mode always bypasses planning: the agent boots straight into
    // building (--dangerously-skip-permissions) and posts its plan to the
    // card before writing code. Use discovery for a human-gated plan turn.
    if (this._mode === "auto" && this.canBypassPlanning(context)) {
      this.transitionToBuilding(context);
      return this._mode;
    }

    return this._mode;
  }

  /**
   * Auto mode always bypasses the plan turn: resolveInitialMode calls
   * transitionToBuilding() at boot, so the agent runs with
   * `--dangerously-skip-permissions` from turn 1. The prompts require it to
   * post its plan to the card (via update_task_plan) before writing code —
   * a record for the team, never an approval gate. Use discovery mode for a
   * plan turn that stops for human approval. Non-auto modes never bypass.
   */
  canBypassPlanning(_context: ModeTaskContext): boolean {
    return this._mode === "auto";
  }

  // ── Mode transitions ───────────────────────────────────────────────

  /** Handle mode change from server */
  handleModeChange(newMode: AgentMode, _context?: ModeTaskContext | null): ModeAction {
    if (newMode === this._mode) return { type: "noop" };

    // Task runners can transition to review mode (same-box code review)
    if (this._runnerMode === "task" && newMode === "review") {
      this._mode = newMode;
      return { type: "restart_query", newMode: "review" };
    }

    // Task runners can transition to building mode (server-side auto-advancement)
    if (this._runnerMode === "task" && newMode === "building") {
      this._mode = newMode;
      this._isAuto = true;
      this._hasExitedPlanMode = true;
      return { type: "restart_query", newMode: "building" };
    }

    // Task runners can transition to auto mode (e.g., Build button after
    // discovery). Auto has no plan turn — build immediately; the plan is
    // documented into the card as the agent works.
    if (this._runnerMode === "task" && newMode === "auto") {
      this._mode = newMode;
      this._isAuto = true;
      this._hasExitedPlanMode = true;
      return { type: "restart_query", newMode: "auto" };
    }

    // Already building in auto mode — server confirmed via wakeAgentForBuilding.
    // Update mode label but don't restart the running query.
    if (this._mode === "auto" && this._hasExitedPlanMode && newMode === "building") {
      this._mode = newMode;
      return { type: "noop" };
    }

    if (this._runnerMode !== "pm") return { type: "noop" };

    this._mode = newMode;
    this.updateExitedPlanModeFlag(newMode);

    if (this.isBuildCapable) {
      return { type: "start_auto" };
    }
    return { type: "noop" };
  }

  /** Handle ExitPlanMode call from the agent */
  handleExitPlanMode(context: ModeTaskContext): ModeAction {
    if (this._hasExitedPlanMode) return { type: "noop" };
    this.transitionToBuilding(context);
    this._pendingModeRestart = true;
    return { type: "restart_query", newMode: this._mode };
  }

  /** Check if pack runner behavior should be used */
  isPackRunner(context: ModeTaskContext): boolean {
    return !!context.isParentTask;
  }

  // ── Internal helpers ───────────────────────────────────────────────

  private transitionToBuilding(context: ModeTaskContext): void {
    this._hasExitedPlanMode = true;
    this._mode = context.isParentTask ? "review" : "building";
  }

  private updateExitedPlanModeFlag(newMode: AgentMode): void {
    if (newMode === "building") {
      this._hasExitedPlanMode = true;
    }
    // For auto mode, we can't set the flag without context — it gets
    // set during context fetch or when ExitPlanMode is called
  }
}
